/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
    afterNextRender,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Injector,
    Input,
    NgZone,
    OnChanges,
    QueryList,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    ViewRef
} from '@angular/core';
import {GanttChart} from '../../model/GanttChart';
import {SvgArea} from '../SvgArea';
import {GanttItem} from '../../model/GanttItem';


@Component({
    selector: 'app-gantt-chart',
    templateUrl: './gantt-chart.component.html',
    styleUrls: ['./gantt-chart.component.css'],
    standalone: false
})
export class GanttChartComponent extends SvgArea implements AfterViewInit, OnChanges {
    @Input() ganttChart: GanttChart; // Add an Input property to receive the GanttChart
    @Input() trueSize: boolean; // Add an Input property to receive a flag indicating this can be wider than the screen
    @ViewChild('ganttChartRootElement') rootElementRefInst: ElementRef;
    @ViewChild('ganttChartSvgElement') svgElementRefInst: ElementRef;
    @ViewChild('scrollbarVertical') scrollbarVerticalRefInst: ElementRef;
    @ViewChild('scrollbarHorizontal') scrollbarHorizontalRefInst: ElementRef;
    @ViewChildren('textInsideElement') textInsideElementsRef: QueryList<ElementRef<SVGTextElement>>;
    @ViewChildren('rectElement') rectElementsRef: QueryList<ElementRef<SVGRectElement>>;

    startDate: Date;
    endDate: Date;
    today = new Date();
    mondays: Date[] = [];
    months: Date[] = [];
    dateFontSize = 8;
    rowHeight = 10;
    public pixelsPerDay = 2;
    public selectedColumn?: Date = null;
    public textScale = Math.pow(this.zoomFactor, this.zoomLevel / 2);
    public readonly highlightDayWindows = [30, 60, 90];
    public readonly upcomingWindows = [
        { days: 15, label: 'Next 30 days' },
        { days: 45, label: '30 - 60 days' },
        { days: 75, label: '60 - 90 days' }
    ];
    public readonly labelYFactors = [1, 3];

    selectColumn(monday: Date): void {
        this.selectedColumn = monday;
    }

    constructor(private elementRef: ElementRef, private renderer: Renderer2,
                private zone: NgZone,
                private cdr: ChangeDetectorRef,
                private injector: Injector) {
        super();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('trueSize' in changes) {
            this.fullSize = !!this.trueSize;
            if (this.fullSize) {
                this.scrollbarVerticalRef = null;
                this.scrollbarHorizontalRef = null;
            } else {
                this.scrollbarVerticalRef = this.scrollbarVerticalRefInst;
                this.scrollbarHorizontalRef = this.scrollbarHorizontalRefInst;
            }
            queueMicrotask(() => this.calculateSvgSize());
        }
        this.renderChart();
    }

    onResize(event: Event): void {
        console.log('Resize event detected');
        this.calculateSvgSize();
    }

    ngAfterViewInit(): void {
        this.zone.run(() => {
            window.addEventListener('resize', this.onResize.bind(this));
        });

        // Assign the instances of ViewChildren to the abstract class's elements:
        this.svgElementRef = this.svgElementRefInst;
        this.rootElementRef = this.rootElementRefInst;

        this.fullSize = !!this.trueSize;

        if (!this.fullSize) {
            this.scrollbarVerticalRef = this.scrollbarVerticalRefInst;
            this.scrollbarHorizontalRef = this.scrollbarHorizontalRefInst;
        }

        super.ngAfterViewInit();
        // After the next render so ViewChildren / layout match what the template will paint.
        afterNextRender(
            () => {
                this.zone.runOutsideAngular(() => {
                    this.correctTextPlacements();
                    this.zone.run(() => {
                        this.cdr.detectChanges();
                    });
                });
            },
            {injector: this.injector},
        );
    }

    adjustSvgSize(): void {
        if (this.fullSize) {
            // Set SVG size to the full content size
            this.displayWidth = this.xExtent * this.scale;
            this.displayHeight = this.yExtent * this.scale;
        } else {
            // Adjust SVG size based on the parent <div>
            this.displayWidth = this.rootElementRef.nativeElement.offsetWidth;
            // this.displayHeight = this.rootElementRef.nativeElement.offsetHeight;
        }
    }

    renderChart() {
        if (this.ganttChart) {
            const range = this.ganttChart.getDateSpan();
            this.startDate = range.startDate;
            this.endDate = range.endDate;

            // Start the rowCount at 3, so the top row is always empty, to show the date labels:
            let rowCount = 3;
            for (const ganttSection of this.ganttChart.ganttSections) {
                // When GanttSections have special dates, allocate 2 rows in which to display those dates.
                // This allocation is only applied after the header row, where the dates are shown.
                const hasSpecialDates = this.ganttChart.showSpecialDates !== false && ganttSection.specialDates.length > 0;
                // When only diamonds are shown (no connector lines), reserve extra spacing so
                // diamonds still have breathing room between program header bars.
                let dateSpacer = !hasSpecialDates
                    ? 0
                    : (this.ganttChart.showSpecialDateLines === false ? 3 : 2);
                for (const ganttItem of ganttSection.ganttItems) {
                    ganttItem.rowPosition = rowCount;
                    rowCount += ganttItem.rowCount + dateSpacer;
                    dateSpacer = 0;
                }
            }
            // Add some extra space at the bottom for the month labels:
            rowCount += 4;

            // widths and heights are in scaled values,
            // xExtent and yExtent are unscales values:
            this.displayWidth = (this.getDateX(this.endDate) - this.getDateX(this.startDate));
            this.xExtent = this.displayWidth / this.scale;
            this.yExtent = rowCount * (this.dateFontSize + 2);

            if (this.fullSize) {
                // Only adjust the height if we are not displaying the full chart
                this.displayHeight = this.yExtent * this.scale;
            }

            // get a list of all the mondays between the startDate and endDate:
            const dayOfWeek = this.startDate.getDay(); // 0=Sunday, 1=Monday, etc.
            let daysOffsetToPriorMonday = 1 - dayOfWeek; // sunday = 1, monday = 0, tu=-1, we=-2, th=-3, fr=-4, sat=-5
            if (daysOffsetToPriorMonday === 1) {
                daysOffsetToPriorMonday = -6;
            }
            const millisecondsInDay = 24 * 60 * 60 * 1000;
            let monday = new Date(this.startDate.getTime() + daysOffsetToPriorMonday * millisecondsInDay);
            this.mondays = [];
            this.months = [];
            while (monday < this.endDate) {
                this.mondays.push(monday);
                // add 7 days to day to get the next monday:
                monday = new Date(monday.getTime() + 7 * 24 * 60 * 60 * 1000);
            }
            // get a list of all the 1st days of the month between the startDate and endDate:
            let first = new Date(this.startDate);
            first.setMonth(first.getMonth(), 1);
            do {
                this.months.push(first);
                first = new Date(first);
                const month = first.getMonth() + 1;
                if (month === 12) {
                    first.setFullYear(first.getFullYear() + 1);
                    first.setMonth(0);
                } else {
                    first.setMonth(month);
                }
            } while (first < this.endDate);
        }
    }

    correctTextPlacements() {
        this.textInsideElementsRef.forEach((textInsideElementRef: ElementRef<SVGTextElement>) => {
            const textInsideElement: SVGTextElement = textInsideElementRef.nativeElement;
            const textId = textInsideElement.getAttribute('id');
            if (!textId) {
                return;
            }

            // Extract the taskId from the text element id:
            const taskId = textId.replace('gantt_text_inside_', '');
            // find the matching ganttItem, <rect> node and right <text> node for the given id
            const ganttItem = [].concat(...this.ganttChart.ganttSections.map(section => section.ganttItems))
                                .find(item => item.taskId === taskId);
            const rectElementRef = this.rectElementsRef.find(ref => ref.nativeElement.id === `gantt_rect_${taskId}`);
            if (rectElementRef && ganttItem) {
                const rectElement: SVGRectElement = rectElementRef.nativeElement;

                // Now you have both the text and rect elements, and you can compare their dimensions
                const textWidth = textInsideElement.getBBox().width;
                const rectWidth = rectElement.getBBox().width;

                // Check if text fits inside the rect
                if (textWidth > rectWidth) {
                    // Text does not fit, display the text to the right of the <rect>
                    ganttItem.textLoc = 'Right';
                } else {
                    // Text fits inside the rect.
                    ganttItem.textLoc = 'Inside';
                }
            }
        });
    }

    onScale(): void {
        // This method will be called anytime the scale factor change
        // this.dateFontSize = 8 * this.objectScale;
        // The text for the mondays, months, and the start & end dates should get larger as you zoom in,
        // but not grow as quickly as the content:
        this.textScale = Math.pow(this.zoomFactor, (this.zoomLevel / 2.0));
    }

    onVerticalScrollbarScroll(event: Event): void {
        const verticalScrollbar = event.target as HTMLElement;
        if (!verticalScrollbar) {
            return;
        }
        if (!this.adjustingScrollbarY) {
            this.yOffset = 0 - verticalScrollbar.scrollTop;
        }
        this.adjustingScrollbarY = false;
    }

    onHorizontalScrollbarScroll(event: Event): void {
        const horizontalScrollbar = event.target as HTMLElement;
        if (!horizontalScrollbar) {
            return;
        }
        if (!this.adjustingScrollbarX) {
            this.xOffset = 0 - horizontalScrollbar.scrollLeft;
        }
        this.adjustingScrollbarX = false;
    }

    protected override requestRender(): void {
        const viewRef = this.cdr as ViewRef;
        if (!viewRef.destroyed) {
            this.cdr.detectChanges();
        }
    }

    getEndDate(ganttItem: GanttItem) {
        return this.getDateX(ganttItem.getDateEnd());
    }
    getDateX(date: Date): number {
        if (date == null || this.startDate == null) {
            return this.xOffset;
        }
        const timeInMills = date.getTime() - this.startDate.getTime();
        const timeInDays = timeInMills / (1000 * 3600 * 24);
        const pixels = timeInDays * this.pixelsPerDay;
        return this.xOffset + pixels * this.scale;
    }
    getDateYearStr(date: Date): string {
        if (date == null) {
            return '';
        }
        const dateYearOptions: Intl.DateTimeFormatOptions = {month: '2-digit', day: '2-digit', year: 'numeric'};
        return date.toLocaleDateString('en-US', dateYearOptions);
    }
    getMonYearStr(date: Date): string {
        if (date == null) {
            return '';
        }
        const monthYearOptions: Intl.DateTimeFormatOptions = {month: 'short', year: 'numeric'};
        return date.toLocaleDateString('en-US', monthYearOptions);
    }
    getDateStr(date: Date, weekOffset?: number): string {
        if (date == null) {
            return '';
        }
        if (weekOffset) {
            // When asking for a week offset, subtract a single day from the results.
            // So this is used like "11/20/24 - 11/26/24" to show a full week, but not double up on the last day
            date = new Date(date.getTime() + (weekOffset * 7 - 1) * 24 * 60 * 60 * 1000);
        }
        const dateOptions: Intl.DateTimeFormatOptions = {day: '2-digit', month: 'short'};
        return date.toLocaleDateString('en-US', dateOptions);
    }
}
